import type { QuotationDocumentData } from '@/quotation/document/types';
import { getLabels } from '@/quotation/document/labels';

interface Props { data: QuotationDocumentData }

interface PartyColProps {
  title: string;
  name?: string;
  rows: { label: string; value: string }[];
}

function PartyCol({ title, name, rows }: PartyColProps) {
  return (
    <div className="flex-1 min-w-0">
      <p className="text-xs font-extrabold text-ink-700 uppercase tracking-widest border-b border-neutral-200 pb-2 mb-3">
        {title}
      </p>
      {name && (
        <p className="text-base font-bold text-neutral-900 leading-snug mb-2.5">{name}</p>
      )}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="text-neutral-400 shrink-0 w-[80px]">{r.label}</span>
            <span className="text-neutral-800 break-words min-w-0">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HtmlDocParties({ data }: Props) {
  const L = getLabels(data.documentLocale);
  const { seller, buyer } = data;

  const sellerRows = [
    ...(seller.address ? [{ label: L.address, value: seller.address }] : []),
    ...(seller.country ? [{ label: L.country, value: seller.country }] : []),
    ...(seller.telephone ? [{ label: L.telephone, value: seller.telephone }] : []),
    ...(seller.email ? [{ label: L.email, value: seller.email }] : []),
    ...(seller.website ? [{ label: L.website, value: seller.website }] : []),
  ];

  const buyerRows = buyer ? [
    ...(buyer.contactName ? [{ label: L.contactPerson, value: buyer.contactName }] : []),
    ...(buyer.address ? [{ label: L.address, value: buyer.address }] : []),
    ...(buyer.country ? [{ label: L.country, value: buyer.country }] : []),
    ...(buyer.email ? [{ label: L.email, value: buyer.email }] : []),
    ...(buyer.phone ? [{ label: L.telephone, value: buyer.phone }] : []),
  ] : [];

  return (
    <div className="px-8 md:px-12 mt-6">
      <div className="flex flex-col md:flex-row gap-6 md:gap-10">
        <PartyCol title={L.seller} name={seller.companyName} rows={sellerRows} />
        {buyer && buyerRows.length > 0 && (
          <>
            <div className="hidden md:block w-px bg-neutral-200" />
            <PartyCol title={L.buyer} name={buyer.companyName} rows={buyerRows} />
          </>
        )}
      </div>
      <div className="mt-6 border-t border-neutral-200" />
    </div>
  );
}